import React from 'react';
import { node, string } from 'prop-types';

import { Global, css } from '@emotion/core';

import {
  animation,
  colors,
  fontSize,
  fontWeight,
  lineHeight,
} from '@datacamp/waffles-tokens';

const Waffles = ({ backgroundColor, children }) => (
  <main>
    <Global
      styles={css`
        /* Port of normalize and base from the design system, removing items that are out of our support level */
        html {
          -ms-text-size-adjust: 100%; /* 2 */
          -webkit-text-size-adjust: 100%; /* 2 */
          box-sizing: border-box;
          font-size: ${fontSize.base};
        }

        * {
          box-sizing: inherit;
        }

        body {
          background-color: ${backgroundColor || colors.porcelain};
          color: ${colors.grey};
          font-family: Lato, -apple-system, BlinkMacSystemFont, 'Segoe UI',
            sans-serif;
          font-size: 100%;
          font-weight: ${fontWeight.regular};
          line-height: ${lineHeight.base};
          margin: 0;
          -webkit-font-smoothing: antialiased;
          -webkit-overflow-scrolling: touch;
        }

        main {
          display: block;
        }

        progress {
          display: inline-block; /* 1 */
          vertical-align: baseline; /* 2 */
        }

        audio:not([controls]) {
          display: none;
          height: 0;
        }

        template {
          display: none;
        }

        a:active,
        a:hover {
          outline: 0;
        }

        :focus {
          outline: 0;
        }

        abbr[title] {
          border-bottom: 1px dotted;
        }

        b,
        strong {
          font-weight: bold;
        }

        dfn {
          font-style: italic;
        }

        mark {
          background: #ff0;
          color: #000;
        }

        small {
          font-size: 80%;
        }

        sub,
        sup {
          font-size: 75%;
          line-height: 0;
          position: relative;
          vertical-align: baseline;
        }

        sup {
          top: -0.5em;
        }

        sub {
          bottom: -0.25em;
        }

        img {
          border: 0;
          max-width: 100%;
        }

        svg:not(:root) {
          overflow: hidden;
        }

        hr {
          box-sizing: content-box;
          height: 0;
        }

        pre {
          overflow: auto;
        }

        code,
        kbd,
        pre,
        samp {
          font-family: monospace, monospace;
          font-size: 1em;
        }

        button,
        input,
        optgroup,
        select,
        textarea {
          color: inherit; /* 1 */
          font: inherit; /* 2 */
          margin: 0; /* 3 */
        }

        button {
          overflow: visible;
        }

        button,
        select {
          text-transform: none;
        }

        button,
        html input[type='button'],
        input[type='reset'],
        input[type='submit'] {
          -webkit-appearance: button; /* 2 */
          cursor: pointer; /* 3 */
        }

        button[disabled],
        html input[disabled] {
          cursor: default;
        }

        button::-moz-focus-inner,
        input::-moz-focus-inner {
          border: 0;
          padding: 0;
        }

        input {
          line-height: normal;
        }

        input[type='checkbox'],
        input[type='radio'] {
          box-sizing: border-box; /* 1 */
          padding: 0; /* 2 */
        }

        input[type='number']::-webkit-inner-spin-button,
        input[type='number']::-webkit-outer-spin-button {
          height: auto;
        }

        input[type='search'] {
          -webkit-appearance: textfield; /* 1 */
          box-sizing: content-box;
        }

        input[type='search']::-webkit-search-cancel-button,
        input[type='search']::-webkit-search-decoration {
          -webkit-appearance: none;
        }

        fieldset {
          border: 1px solid #c0c0c0;
          margin: 0 2px;
          padding: 0.35em 0.625em 0.75em;
        }

        legend {
          border: 0; /* 1 */
          padding: 0; /* 2 */
        }

        textarea {
          overflow: auto;
        }

        optgroup {
          font-weight: bold;
        }

        table {
          border-collapse: collapse;
          border-spacing: 0;
        }

        td,
        th {
          padding: 0;
        }

        /* Link from design/typography/_link.scss */
        a,
        .dc-link {
          border-bottom: 1px solid transparent;
          color: ${colors.primary};
          font-weight: ${fontWeight.bold};
          text-decoration: none;
          transition: border-color ${animation.transition};

          &:hover {
            border-bottom-color: ${colors.primary};
            color: ${colors.primary};
            text-decoration: none;
          }
        }

        /* Typography from design/typography/_typography-legacy.scss */

        code {
          color: ${colors.greyDark};
          display: inline-block;
          font-size: ${fontSize.small};
          line-height: 1;
          margin: 0 4px;
          padding: 4px 6px;
        }

        pre {
          border-left: 4px solid ${colors.primary};
          line-height: 1.8;
          overflow-x: auto;
          padding: 16px;
          transition: max-width ${animation.transition},
            margin ${animation.transition};
        }

        pre code {
          line-height: ${lineHeight.heading};
        }

        mark {
          color: ${colors.primaryDark};
          font-weight: ${fontWeight.bold};
          margin: 0 0.3em;
          padding: 0.3em 0.4em;

          &:selection {
            background-color: ${colors.primaryDark};
            color: $dc-white;
          }
        }

        dd {
          margin-bottom: 1.2em;
          margin-left: 0;
        }

        dt {
          color: ${colors.primary};
          font-weight: ${fontWeight.bold};
          text-transform: uppercase;
        }

        h1,
        .dc-h1,
        h2,
        .dc-h2,
        h3,
        .dc-h3,
        h4,
        .dc-h4,
        h5,
        .dc-h5,
        h6,
        .dc-h6 {
          color: ${colors.greyDark};
          font-weight: ${fontWeight.bold};
          line-height: ${lineHeight.heading};
          margin: 0.25em 0;
        }

        h1,
        .dc-h1 {
          font-size: ${fontSize.h1};
        }

        h2,
        .dc-h2 {
          font-size: ${fontSize.h2};
        }

        h3,
        .dc-h3 {
          font-size: ${fontSize.h3};
        }

        h4,
        .dc-h4 {
          font-size: ${fontSize.h4};
        }

        h5,
        .dc-h5 {
          font-size: ${fontSize.h5};
        }

        h6,
        .dc-h6 {
          font-size: ${fontSize.base};
          text-transform: uppercase;
        }

        .chapeau-title,
        .dc-chapeau-title {
          font-size: ${fontSize.small};
          font-weight: ${fontWeight.bold};
          letter-spacing: 2px;
          text-transform: uppercase;
        }
      `}
    />
    {children}
  </main>
);

Waffles.defaultProps = {
  backgroundColor: null,
};

Waffles.propTypes = {
  children: node.isRequired,
  backgroundColor: string,
};

export default Waffles;
